/**
 * TopStatesSection - Region choropleth map + sales share table with comparison
 * Supports US (states), CA (provinces), UK (regions)
 */

import React, { useMemo } from 'react';
import type { StateData, MarketplaceCode } from '../../types/transaction';
import { getRegionName, getRegionLabel } from '../../constants/postalToRegion';
import RegionMap from './RegionMap';
import type { USStateMapData } from './RegionMap';

interface TopStatesSectionProps {
  marketplace: MarketplaceCode;
  stateDistribution: Record<string, StateData>;
  comparisonStateDistribution: Record<string, StateData> | null;
  formatMoney: (value: number) => string;
}

const TopStatesSection: React.FC<TopStatesSectionProps> = ({
  marketplace,
  stateDistribution,
  comparisonStateDistribution,
  formatMoney,
}) => {
  const labels = getRegionLabel(marketplace);

  // Convert to map data
  const mapData = useMemo(() => {
    const result: Record<string, USStateMapData> = {};
    for (const [code, data] of Object.entries(stateDistribution)) {
      result[code] = {
        value: data.salesShare,
        label: `${data.salesShare.toFixed(2)}% share`,
        orders: data.count,
        revenue: data.sales,
      };
    }
    return result;
  }, [stateDistribution]);

  // Sort by sales share descending, take top 15
  const sortedRegions = Object.entries(stateDistribution)
    .filter(([code]) => code !== 'AP' && code !== 'GU' && code !== 'VI')
    .sort((a, b) => b[1].salesShare - a[1].salesShare)
    .slice(0, 15);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6 print-block print-card">
      <h3 className="text-lg font-semibold text-slate-800 mb-4">{labels.title}</h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        {/* Map */}
        <div>
          <RegionMap marketplace={marketplace} stateData={mapData} colorMode="revenue" formatMoney={formatMoney} />
        </div>

        {/* Table */}
        <div>
          <div className="grid grid-cols-3 gap-2 mb-2 px-2">
            <span className="text-xs font-semibold text-blue-500 uppercase">{labels.singular}</span>
            <span className="text-xs font-semibold text-blue-500 uppercase text-center">Sales Share</span>
            {comparisonStateDistribution && (
              <span className="text-xs font-semibold text-blue-500 uppercase text-right">Change</span>
            )}
          </div>

          <div className="space-y-0">
            {sortedRegions.map(([code, data]) => {
              const prevData = comparisonStateDistribution?.[code];
              const change = prevData ? data.salesShare - prevData.salesShare : null;

              return (
                <div
                  key={code}
                  className="grid grid-cols-3 gap-2 items-center py-2.5 px-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-700">
                    {getRegionName(code, marketplace)}
                  </span>

                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-sm font-semibold text-slate-800 w-14 text-right">
                      {data.salesShare.toFixed(2)}%
                    </span>
                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                      <div
                        className="h-full bg-blue-400 rounded-full"
                        style={{ width: `${Math.min((data.salesShare / (sortedRegions[0]?.[1]?.salesShare || 1)) * 100, 100)}%` }}
                      />
                    </div>
                  </div>

                  {comparisonStateDistribution && (
                    <div className="text-right">
                      {change !== null ? (
                        <span className={`text-xs font-medium inline-flex items-center gap-0.5 ${
                          change > 0.01 ? 'text-green-600' : change < -0.01 ? 'text-red-500' : 'text-slate-400'
                        }`}>
                          {change > 0.01 ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                            </svg>
                          ) : change < -0.01 ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                          ) : null}
                          {change > 0.01 ? '+' : ''}{change.toFixed(2)}%
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">new</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 pt-2 border-t border-slate-200">
            <p className="text-xs text-slate-500">
              Top {sortedRegions.length} {labels.plural.toLowerCase()} cover{' '}
              <span className="font-semibold">
                {sortedRegions.reduce((sum, [, d]) => sum + d.salesShare, 0).toFixed(1)}%
              </span>{' '}
              of total sales
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TopStatesSection);
